import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Cross from '@src/assets/svg/cross';
import { Field, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';
import TextField from '@src/components/coreUI/textField';
import Button from '@src/components/coreUI/button';
import { VALIDATION_MESSAGES } from '@src/constants/messages';
import { APP_ROUTE } from '@src/constants/routes';
import { EMAIL_PATTERN } from '@src/constants/common';

interface ContactFormTypes {
  fullName: string;
  email: string;
  phoneNumber: string;
  companyName: string;
}

const initialValues: ContactFormTypes = {
  fullName: '',
  email: '',
  phoneNumber: '',
  companyName: '',
};

const contactFormSchema = Yup.object().shape({
  fullName: Yup.string().required(VALIDATION_MESSAGES.required),
  email: Yup.string()
    .matches(EMAIL_PATTERN, VALIDATION_MESSAGES.validEmail)
    .required(VALIDATION_MESSAGES.required),
  phoneNumber: Yup.string()
    .required(VALIDATION_MESSAGES.required)
    .matches(
      /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
      VALIDATION_MESSAGES.validPhone
    )
    .required(VALIDATION_MESSAGES.required),
  companyName: Yup.string().required(VALIDATION_MESSAGES.required),
});

const AdvertisementContactFunnel = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  // eslint-disable-next-line no-unused-vars
  onClose: (value: ContactFormTypes | undefined) => void;
}) => {
  /**
   * TODO: We need to plant another script to handle contact form @ben has to plant this script
   *
   * @param {ContactFormTypes} values - description of parameter
   * @return {void} description of return value
   */
  const handleSubmit = (values: ContactFormTypes) => {
    onClose(values);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[100]"
        onClose={() => {
          return;
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black-dark/30 transition-opacity backdrop-blur-[1px]" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-xl bg-white pt-5 text-left shadow-xl transition-all sm:my-8 w-full sm:w-full max-w-full sm:max-w-lg py-8 sm:px-8 px-5">
                <div>
                  <a
                    onClick={() => onClose(undefined)}
                    className="absolute block w-[1.875rem] h-[1.875rem] top-[-10px] right-[-7px] rounded-full text-blue-dark hover:text-blue-dark/70 focus-visible:outline-none cursor-pointer"
                  >
                    <Cross />
                  </a>
                  {/* Modal title */}
                  <Dialog.Title
                    as="h3"
                    className="text-secondary-dark font-semibold text-22 mt-5"
                  >
                    Try the Zeme Agent Platform!
                  </Dialog.Title>

                  {/* Modal body */}
                  <div className="max-w-400px m-auto mt-6">
                    <Formik
                      initialValues={initialValues}
                      validationSchema={contactFormSchema}
                      onSubmit={handleSubmit}
                    >
                      <Form>
                        <div className="mb-5">
                          <Field
                            id="fullName"
                            name="fullName"
                            render={({
                              field: { name, value, onChange, onBlur },
                              form: { touched, errors },
                            }: FieldProps) => (
                              <TextField
                                error={Boolean(touched[name] && errors[name])}
                                label="Full Name"
                                value={value}
                                helperText={
                                  Boolean(touched[name] && errors[name])
                                    ? String(errors[name])
                                    : ''
                                }
                                inputProps={{
                                  name: 'fullName',
                                  autoComplete: 'fullName',
                                  placeholder: 'Enter Full Name',
                                  onBlur,
                                }}
                                onChange={onChange}
                              />
                            )}
                          />
                        </div>
                        <div className="mb-5">
                          <Field
                            id="email"
                            name="email"
                            render={({
                              field: { name, value, onChange, onBlur },
                              form: { touched, errors },
                            }: FieldProps) => (
                              <TextField
                                error={Boolean(touched[name] && errors[name])}
                                label="Business Email"
                                value={value}
                                helperText={
                                  Boolean(touched[name] && errors[name])
                                    ? String(errors[name])
                                    : ''
                                }
                                inputProps={{
                                  name: 'email',
                                  autoComplete: 'email',
                                  placeholder: 'Enter Business Email',
                                  onBlur,
                                }}
                                onChange={onChange}
                              />
                            )}
                          />
                        </div>
                        <div className="mb-5">
                          <Field
                            id="phoneNumber"
                            name="phoneNumber"
                            render={({
                              field: { name, value, onChange, onBlur },
                              form: { touched, errors },
                            }: FieldProps) => (
                              <TextField
                                mask="(999) 999-9999"
                                type="text"
                                error={Boolean(touched[name] && errors[name])}
                                label="Phone Number"
                                value={value}
                                helperText={
                                  Boolean(touched[name] && errors[name])
                                    ? String(errors[name])
                                    : ''
                                }
                                inputProps={{
                                  name: 'phoneNumber',
                                  autoComplete: 'phoneNumber',
                                  onBlur,
                                  placeholder: 'Enter Phone Number',
                                }}
                                onChange={onChange}
                              />
                            )}
                          />
                        </div>
                        <div className="mb-5">
                          <Field
                            id="companyName"
                            name="companyName"
                            render={({
                              field: { name, value, onChange, onBlur },
                              form: { touched, errors },
                            }: FieldProps) => (
                              <TextField
                                error={Boolean(touched[name] && errors[name])}
                                label="Company Name"
                                value={value}
                                helperText={
                                  Boolean(touched[name] && errors[name])
                                    ? String(errors[name])
                                    : ''
                                }
                                inputProps={{
                                  name: 'companyName',
                                  autoComplete: 'companyName',
                                  placeholder: 'Enter Company Name',
                                  onBlur,
                                }}
                                onChange={onChange}
                              />
                            )}
                          />
                        </div>
                        <div className="mb-5">
                          <p className="text-sm text-gray-500 text-center">
                            By submitting this form, you agreeing to Zeme Inc’s
                            {` `}
                            <a
                              className="font-bold underline"
                              target="blank"
                              href={`${APP_ROUTE.PRIVACY}`}
                            >
                              Privacy Policy
                            </a>{' '}
                            and{' '}
                            <a
                              className="font-bold underline"
                              target="blank"
                              href={`${APP_ROUTE.TU_AGENT_TERMS}`}
                            >
                              Terms and Conditions
                            </a>
                          </p>
                        </div>
                        <div className="flex justify-center mt-12">
                          <Button type="submit" size="md" variant="primary">
                            Get Started
                          </Button>
                        </div>
                      </Form>
                    </Formik>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AdvertisementContactFunnel;
